import { useMemo } from "react";

import { PrimaryButton } from "@components/v2/buttons";
import { useHiddenBudgetMixes } from "@features/mmm/hooks/use-hidden-budget-mixes";
import { useBudgetMixes } from "@pages/mmm/hooks/use-budget-mixes";
import { useClientSettingsColorMap } from "@pages/mmm/hooks/use-client-settings-color-map";

import { useBudgetMix } from "../budget-mix-card/use-budget-mix";

import { ClientSettingCard } from "./client-setting-card";
import { HistoricalClientSettingChooser } from "./historical-client-setting-chooser";
import { type ClientSetting } from "./types";

export function ClientSettingsContainer({
  canUpdate,
  canAlter,
  clientSettings,
  onCreateNewClientSetting,
  onEditClientSetting,
}: {
  canUpdate: boolean;
  canAlter: boolean;
  clientSettings: ClientSetting[];
  onCreateNewClientSetting: () => void;
  onEditClientSetting: (clientSettingToEdit: ClientSetting) => void;
}) {
  const clientSettingsColorMap = useClientSettingsColorMap();

  const [hiddenBudgetMixes] = useHiddenBudgetMixes();

  const [budgetMixes] = useBudgetMixes();

  const clientSettingsToShow = useMemo(() => {
    return clientSettings.filter(
      (setting) => !hiddenBudgetMixes.includes(setting.id),
    );
  }, [clientSettings, hiddenBudgetMixes]);

  const clientSettingsHaveBudgetMixes = useMemo(() => {
    const clientSettingToBudgetMixExistance: Record<string, boolean> = {};
    budgetMixes.forEach((budgetMix) => {
      clientSettingToBudgetMixExistance[budgetMix.client_setting_id] = true;
    });
    return clientSettingToBudgetMixExistance;
  }, [budgetMixes]);

  const canEdit = (clientSetting: ClientSetting) => {
    return !clientSettingsHaveBudgetMixes[clientSetting.id];
  };

  return (
    <div className="flex flex-col mb-5 ">
      {clientSettingsToShow.map((setting) => (
        <ClientSettingCard
          clientSetting={setting}
          canAlter={canAlter}
          canEdit={canEdit(setting)}
          onEditClientSetting={onEditClientSetting}
          color={clientSettingsColorMap[setting.id]}
          key={setting.id}
        />
      ))}
      {canUpdate && (
        <div className="mt-2">
          <ClientSettingUpdateControls
            onCreateNewClientSetting={onCreateNewClientSetting}
          />
        </div>
      )}
    </div>
  );
}

function ClientSettingUpdateControls({
  onCreateNewClientSetting,
}: {
  onCreateNewClientSetting: () => void;
}) {
  return (
    <div className="flex w-fit justify-start items-center gap-4">
      <PrimaryButton
        className="flex items-center justify-center gap-2"
        onClick={onCreateNewClientSetting}
      >
        <i className="fa-regular fa-plus" />
        NEW SETTING
      </PrimaryButton>
      <HistoricalClientSettingChooser />
    </div>
  );
}
